import React from 'react';
import {
  Lightbulb,
  CheckCircle2,
  Hammer,
  Package,
  Wrench,
  CreditCard,
  Receipt,
  ListChecks,
  Banknote,
  Puzzle,
  AlertTriangle,
  EyeOff,
  Route,
  CalendarX,
  TrendingUp,
  Gavel,
  Eye,
  Activity,
  Database,
  Lock,
  SearchX,
  UserX,
  ShieldAlert,
  KeyRound,
  ArrowRightLeft,
  Layers,
  Landmark,
  Users,
  Presentation,
  ClipboardList,
  CalendarDays,
} from 'lucide-react';

import Advice from '../../components/Advice';
import { FocusChoice } from '../../components/Capture';

/* ======================================================
   NobleOak — free advice slides
   Practical guidance NobleOak can act on with or without Optiii.
====================================================== */

const Check = ({ children }) => (
  <div className="check">
    <CheckCircle2 size={24} />
    <span>{children}</span>
  </div>
);

const AdviceEyebrow = ({ children }) => (
  <div className="eyebrow adviceEyebrow">
    <Lightbulb size={24} />
    {children}
  </div>
);





const adviceSlides = [
  /* Why AI in finance is harder — and pays off more */
  {
    id: 'why-finance-hard',
    content: (
      <>
        <div className="head" style={{ marginBottom: 30 }}>
          <div className="eyebrow">What stands in the way</div>
          <h2 style={{ margin: 0 }}>AI can close the gap — if finance gets it right</h2>
        </div>
        <div className="grid" style={{ gridTemplateColumns: '1.15fr 0.85fr', gap: 36, alignItems: 'stretch' }}>
          <div style={{ display: 'grid', gap: 10 }}>
            {[
              [Puzzle, 'Finance is where disconnected data gets fixed', 'Gaps between policy, partner and ledger systems are bridged by people — AI inherits that mess.'],
              [AlertTriangle, 'Mistakes are expensive', 'A wrong number reaches the board, the market or APRA. Accuracy has to be near-perfect and provable.'],
              [EyeOff, 'The data is sensitive', 'Payroll, policyholder and price-sensitive results can’t simply be shared with engineers or vendors.'],
              [Route, 'Every number must be traceable', 'Auditors and regulators need each figure linked to its source, its logic and an approval.'],
              [CalendarX, 'Deadlines don’t move', 'Close, results and APRA dates are fixed — there’s no room to experiment on the critical path.'],
            ].map(([Ic, t, d]) => (
              <div className="advRow" key={t} style={{ gridTemplateColumns: '1fr', padding: '13px 22px', gap: 4 }}>
                <b><Ic size={22} style={{ color: '#FCD34D' }} />{t}</b>
                <span>{d}</span>
              </div>
            ))}
          </div>
          <div className="card payoff" style={{ padding: 32, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="colTitle" style={{ marginBottom: 4 }}><TrendingUp size={28} style={{ color: '#86EFAC' }} />Get those right, and finance gains most</div>
            <div className="checks" style={{ gap: 14 }}>
              <Check>High-volume, rules-based work — reconciliations, matching, journals</Check>
              <Check>Structured data with known answers, so AI output can be tested properly</Check>
              <Check>Narrative work AI drafts well — variance commentary, board papers, Q&amp;A prep</Check>
              <Check>Every hour saved moves to analysis the business actually values</Check>
            </div>
            <div style={{ marginTop: 'auto', paddingTop: 18, borderTop: '1px solid rgba(134,239,172,0.25)', fontSize: 21, lineHeight: 1.5, color: '#fff' }}>
              Finance has the clearest “right answer” of any function — which makes it the best place to prove AI safely.
            </div>
          </div>
        </div>
        <Advice style={{ marginTop: 22 }}>
          How we get past the hard parts: <b>fix the data layer first</b>, keep a person approving anything that posts,
          share only the data each task needs, trace every output to source, and <b>start off the critical path</b>.
        </Advice>
      </>
    ),
  },

  /* Build vs buy — our view */
  {
    id: 'advice-build-buy',
    content: (
      <>
        <div className="head" style={{ marginBottom: 36 }}>
          <AdviceEyebrow>Free advice · build vs buy</AdviceEyebrow>
          <h2 style={{ margin: 0 }}>Building is cheaper now. Owning it is the catch.</h2>
        </div>
        <div className="grid g2" style={{ gap: 28, marginBottom: 26 }}>
          <div className="card" style={{ padding: 36 }}>
            <div className="colTitle"><Hammer size={28} style={{ color: '#C4B5FD' }} />Why building now wins more often</div>
            <div className="checks" style={{ gap: 14 }}>
              <Check>AI-assisted development turns months of build into weeks for internal finance tools</Check>
              <Check>Logic fits NobleOak exactly — fund structures, partner terms, APRA mappings</Check>
              <Check>No per-seat licences, and your data stays in your environment</Check>
              <Check>Most niche finance SaaS is a thin layer you can now build and own</Check>
            </div>
          </div>
          <div className="card" style={{ padding: 36 }}>
            <div className="colTitle"><Wrench size={28} style={{ color: '#FCD34D' }} />The maintenance problem</div>
            <div className="checks" style={{ gap: 14 }}>
              <Check>Source systems, APIs and AI models change — someone must keep it working</Check>
              <Check>Without tests and documentation, a tool becomes one person’s black box</Check>
              <Check>Key-person risk moves from the spreadsheet owner to the developer</Check>
              <Check>Budget ongoing time, not just the build</Check>
            </div>
          </div>
        </div>
        <Advice>
          <b>Build where your logic is unique; buy where a vendor moves faster than you can.</b> If you build, decide the
          owner, tests, runbook and monitoring before the first line of code — and keep the model swappable.
        </Advice>
      </>
    ),
  },

  /* Where buying is faster */
  {
    id: 'advice-fast-tools',
    footnote: 'Examples to evaluate, not endorsements. We’re tooling-agnostic but opinionated.',
    content: (
      <>
        <div className="head" style={{ marginBottom: 30 }}>
          <AdviceEyebrow>Free advice · where buying is faster</AdviceEyebrow>
          <h2 style={{ margin: 0 }}>Don’t build what a specialist does better</h2>
        </div>
        <div className="advHead" style={{ gridTemplateColumns: '0.95fr 1.25fr 1fr 0.45fr' }}>
          <span>Area</span><span>Why buy</span><span>Examples to evaluate</span><span>Call</span>
        </div>
        <div className="advTable">
          {[
            [CreditCard, 'Expense management & cards', 'Card controls, receipt capture and GST coding out of the box; built on banking rails you can’t replicate', 'Weel, Airwallex (AU) — Ramp sets the benchmark (US/UK)', 'buy'],
            [Receipt, 'Accounts payable', 'Invoice capture, approval flows and ledger sync are commoditised and improve monthly', 'Lightyear, ApprovalMax', 'buy'],
            [ListChecks, 'Close management', 'Checklists, sign-offs and audit evidence at scale; auditors know these tools', 'BlackLine, FloQast, Numeric', 'buy'],
            [Banknote, 'Bank data', 'Daily statements in standard formats directly from your banks', 'Your banks’ direct file feeds', 'buy'],
            [ArrowRightLeft, 'Insurer-specific recs & partner commissions', 'Premium, reinsurance and alliance logic is unique to NobleOak — no product fits it well', 'Build on your data layer', 'build'],
            [Landmark, 'APRA data prep & board narrative', 'Mappings and commentary that change with your structure and the Life Company transition', 'Build, with AI drafting', 'build'],
          ].map(([Ic, area, why, tools, call]) => (
            <div className="advRow" key={area} style={{ gridTemplateColumns: '0.95fr 1.25fr 1fr 0.45fr' }}>
              <b><Ic size={24} />{area}</b>
              <span>{why}</span>
              <span className="tools">{tools}</span>
              <span><span className={`pill ${call}`}>{call === 'buy' ? <Package size={16} /> : <Hammer size={16} />}{call === 'buy' ? 'Buy' : 'Build'}</span></span>
            </div>
          ))}
        </div>
      </>
    ),
  },



  /* APRA's AI expectations */
  {
    id: 'advice-apra-ai',
    footnote: 'Source: APRA letter to industry on AI, 30 April 2026. A summary for planning, not legal or regulatory advice.',
    content: (
      <>
        <div className="head" style={{ marginBottom: 30 }}>
          <AdviceEyebrow>Free advice · APRA &amp; AI</AdviceEyebrow>
          <h2 style={{ margin: 0 }}>Every AI output has to stand up to APRA</h2>
        </div>
        <div className="grid g3" style={{ gap: 20, marginBottom: 22 }}>
          {[
            [Gavel, 'Board accountability', 'AI literacy, a strategy within risk appetite, and triggers for action', 'Each workflow gets a one-page summary and triggers the board can use'],
            [ClipboardList, 'Inventory & ownership', 'A register of AI tools and use cases, owned from design to retirement', 'Every workflow we build is registered with a named owner'],
            [ArrowRightLeft, 'Suppliers, including model providers', 'Map the AI supply chain, manage concentration, test exit plans', 'A swappable model layer and a documented exit plan'],
            [KeyRound, 'Security for agents', 'Identities for non-human actors, controls on agentic workflows, tested AI-written code', 'Agents run on their own least-privilege identities; code is reviewed and tested'],
            [Activity, 'Continuous assurance', 'Ongoing monitoring for drift and failure, not point-in-time checks', 'Monitoring and exception reporting built in from day one'],
            [Eye, 'Human oversight & data', 'People accountable for high-risk decisions; privacy and bias managed', 'Human approval on postings; personal data stripped before any model'],
          ].map(([Ic, t, expects, we]) => (
            <div className="card" key={t} style={{ padding: 26, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <b style={{ fontSize: 23, display: 'flex', alignItems: 'center', gap: 12 }}><Ic size={24} style={{ color: '#C4B5FD' }} />{t}</b>
              <div style={{ fontSize: 18, color: 'rgba(245,245,247,0.72)', lineHeight: 1.45 }}>{expects}</div>
              <div style={{ fontSize: 18, color: '#86EFAC', lineHeight: 1.45, display: 'flex', gap: 8 }}><CheckCircle2 size={18} style={{ flex: 'none', marginTop: 3 }} />{we}</div>
            </div>
          ))}
        </div>
        <Advice>
          APRA has signalled closer supervision of AI. <b>Start your AI register and supplier map now.</b> Our rule:
          if an output can’t be traced to source and defended in a room, it doesn’t ship.
        </Advice>
      </>
    ),
  },

  /* Clean data & security — the biggest gap */
  {
    id: 'advice-data',
    content: (
      <>
        <div className="head" style={{ marginBottom: 24 }}>
          <AdviceEyebrow>Free advice · the biggest gap</AdviceEyebrow>
          <h2 style={{ margin: 0 }}>Data and security slow projects — not AI</h2>
        </div>
        <div className="grid" style={{ gridTemplateColumns: '0.95fr 1.05fr', gap: 40, alignItems: 'start' }}>
          <div style={{ display: 'grid', gap: 10 }}>
            {[
              [SearchX, 'No single source of truth', 'The same number lives in three spreadsheets, each slightly different.'],
              [Lock, 'Access and security reviews', 'Read-only access to the ledger and policy data takes weeks to approve.'],
              [Database, 'Quality problems found late', 'Mapping gaps surface mid-build, not in week one.'],
              [UserX, 'Nobody owns the data', 'Finance assumes IT does; IT assumes finance does.'],
            ].map(([Ic, t, d]) => (
              <div className="advRow" key={t} style={{ gridTemplateColumns: '1fr', padding: '11px 22px', gap: 4 }}>
                <b><Ic size={24} />{t}</b>
                <span>{d}</span>
              </div>
            ))}
          </div>
          <div className="soft" style={{ padding: 32 }}>
            <FocusChoice id="dataTeam" columns={2} prompt="Select one">
              {(team) => (
                <div className="checks" style={{ gap: 16 }}>
                  {{
                    'Dedicated data team': [
                      'We plug into their warehouse, standards and security model',
                      'Finance automations become data products they can support',
                      'Their review happens once, early — not per workflow',
                    ],
                    'Analysts within finance': [
                      'We build on what they already know and maintain',
                      'Add a light, governed data layer underneath their work',
                      'Train them as the owners after handover',
                    ],
                    'IT, as needed': [
                      'Agree access and the security review in week one',
                      'Propose one data layer IT approves once, not per request',
                      'Keep ownership clear between finance and IT',
                    ],
                    'No dedicated resource': [
                      'Keep the data layer simple and managed',
                      'Build ownership and handover into the plan from day one',
                      'Size any ongoing support before you commit',
                    ],
                  }[team].map((t) => <Check key={t}>{t}</Check>)}
                </div>
              )}
            </FocusChoice>
          </div>
        </div>
        <Advice style={{ marginTop: 18 }}>
          <b>Start access and security approvals before the project does.</b> Read-only access requests are the most
          common reason finance automation timelines slip.
        </Advice>
      </>
    ),
  },


  /* Life Company transition checklist */
  {
    id: 'advice-life-co',
    footnote: 'General guidance for planning; confirm requirements with APRA guidance and your auditors.',
    content: (
      <>
        <div className="head" style={{ marginBottom: 34 }}>
          <AdviceEyebrow>Free advice · Life Company transition</AdviceEyebrow>
          <h2 style={{ margin: 0 }}>And it all needs to land before December 2027</h2>
        </div>
        <div className="grid g2" style={{ gap: 28, marginBottom: 26 }}>
          <div className="card" style={{ padding: 34 }}>
            <div className="colTitle"><Layers size={28} style={{ color: '#C4B5FD' }} />Structure &amp; data</div>
            <div className="checks" style={{ gap: 14 }}>
              <Check>Design the target chart of accounts for one statutory fund early — it drives everything else</Check>
              <Check>Map every benefit-fund balance and flow to the new structure, and keep the history queryable</Check>
              <Check>Update regulatory reporting data mappings at the same time, not after</Check>
            </div>
          </div>
          <div className="card" style={{ padding: 34 }}>
            <div className="colTitle"><ShieldAlert size={28} style={{ color: '#C4B5FD' }} />Controls &amp; cut-over</div>
            <div className="checks" style={{ gap: 14 }}>
              <Check>Plan at least one parallel close on the new structure before cut-over</Check>
              <Check>Agree the audit approach for the transition with your auditor early</Check>
              <Check>Document controls and owners for every new or changed process</Check>
            </div>
          </div>
        </div>
        <Advice>
          <b>Automate on the new structure, not the old one.</b> Anything built against today’s benefit funds will need
          rework — sequence automation so it lands on the target chart of accounts.
        </Advice>
      </>
    ),
  },


  /* Project cadence — for the Project Manager */
  {
    id: 'project-cadence',
    content: (
      <>
        <div className="head" style={{ marginBottom: 34 }}>
          <div className="eyebrow">Running the project</div>
          <h2 style={{ margin: 0 }}>A cadence your project manager can run with</h2>
        </div>
        <div className="grid" style={{ gridTemplateColumns: '1.2fr 0.8fr', gap: 28, marginBottom: 24 }}>
          <div className="advTable">
            {[
              [Users, 'Weekly working session', 'Finance leads + Optiii', 'Decisions, blockers, next week’s builds'],
              [Presentation, 'Fortnightly demo', 'Finance team + PM', 'Show what’s built; test on real data'],
              [CalendarDays, 'Monthly steering', 'CFO, Head of AI & IT, PM', 'Progress vs plan, risks, scope calls'],
            ].map(([Ic, t, who, what]) => (
              <div className="advRow" key={t} style={{ gridTemplateColumns: '1fr 0.9fr 1.1fr' }}>
                <b><Ic size={24} />{t}</b>
                <span className="tools">{who}</span>
                <span>{what}</span>
              </div>
            ))}
          </div>
          <div className="card" style={{ padding: 32 }}>
            <div className="colTitle"><ClipboardList size={28} style={{ color: '#C4B5FD' }} />What the PM gets</div>
            <div className="checks" style={{ gap: 12 }}>
              <Check>Shared backlog and delivery plan</Check>
              <Check>RAID log and decision log</Check>
              <Check>Test evidence for every release</Check>
              <Check>Weekly one-page status</Check>
            </div>
          </div>
        </div>
        <Advice>
          <b>Optiii builds and trains; NobleOak owns and approves.</b> Name a business owner for every workflow before it
          goes live — it’s the single best predictor of whether automation is still running a year later.
        </Advice>
      </>
    ),
  },
];

export default adviceSlides;
